import { useState } from 'react';
import { MapPin, Search, Thermometer, Wind, Droplets } from 'lucide-react';
import type { CookDetails, WeatherData } from '../../types';
import { WeatherService } from '../../services/weather.service';

interface Props {
  data: Partial<CookDetails>;
  onChange: (data: Partial<CookDetails>) => void;
  onNext: () => void;
}

export default function LocationInput({ data: _data, onChange, onNext: _onNext }: Props) {
  const [locationType, setLocationType] = useState<'zip' | 'city' | 'coords'>('zip');
  const [zipCode, setZipCode] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [coords, setCoords] = useState({ lat: '', lon: '' });
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLocationTypeChange = (type: 'zip' | 'city' | 'coords') => {
    setLocationType(type);
    // Clear weather when switching types
    setWeather(null);
    setError(null);
  };

  const handleCheckWeather = async () => {
    setLoading(true);
    setError(null);
    setWeather(null);

    try {
      const weatherService = new WeatherService();
      let location: CookDetails['location'];

      if (locationType === 'zip' && zipCode) {
        location = { type: 'zip', value: zipCode };
      } else if (locationType === 'city' && city && state) {
        location = { type: 'city', value: `${city}, ${state}` };
      } else if (locationType === 'coords' && coords.lat && coords.lon) {
        location = {
          type: 'coords',
          value: `${coords.lat},${coords.lon}`,
          lat: parseFloat(coords.lat),
          lon: parseFloat(coords.lon),
        };
      } else {
        setError('Please enter a location');
        setLoading(false);
        return;
      }

      const weatherData = await weatherService.getWeather(location);
      setWeather(weatherData);
      onChange({ location });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch weather';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const useCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude.toFixed(4);
          const lon = position.coords.longitude.toFixed(4);
          setCoords({ lat, lon });
          setLocationType('coords');
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location. Please enter it manually.');
        }
      );
    }
  };

  return (
    <div className="space-y-4">
      {/* Location Type Selector */}
      <div className="flex gap-2">
        <button
          onClick={() => handleLocationTypeChange('zip')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
            locationType === 'zip'
              ? 'bg-flame-600 text-white'
              : 'bg-smoke-100 text-smoke-700 hover:bg-smoke-200'
          }`}
        >
          ZIP Code
        </button>
        <button
          onClick={() => handleLocationTypeChange('city')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
            locationType === 'city'
              ? 'bg-flame-600 text-white'
              : 'bg-smoke-100 text-smoke-700 hover:bg-smoke-200'
          }`}
        >
          City
        </button>
        <button
          onClick={() => handleLocationTypeChange('coords')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
            locationType === 'coords'
              ? 'bg-flame-600 text-white'
              : 'bg-smoke-100 text-smoke-700 hover:bg-smoke-200'
          }`}
        >
          Coords
        </button>
      </div>

      {/* Input Based on Type */}
      {locationType === 'zip' && (
        <input
          type="text"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && zipCode && handleCheckWeather()}
          placeholder="Enter ZIP code (e.g., 97204)"
          className="input-field"
          maxLength={10}
        />
      )}

      {locationType === 'city' && (
        <div className="space-y-3">
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && city && state && handleCheckWeather()}
            placeholder="City (e.g., Portland)"
            className="input-field"
          />
          <input
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && city && state && handleCheckWeather()}
            placeholder="State (e.g., OR)"
            className="input-field"
            maxLength={2}
          />
        </div>
      )}

      {locationType === 'coords' && (
        <div className="space-y-3">
          <input
            type="text"
            value={coords.lat}
            onChange={(e) => setCoords({ ...coords, lat: e.target.value })}
            placeholder="Latitude (e.g., 45.5200)"
            className="input-field"
          />
          <input
            type="text"
            value={coords.lon}
            onChange={(e) => setCoords({ ...coords, lon: e.target.value })}
            placeholder="Longitude (e.g., -122.6800)"
            className="input-field"
          />
        </div>
      )}

      {/* Current Location Button */}
      <button
        onClick={useCurrentLocation}
        className="w-full py-3 border-2 border-smoke-300 rounded-lg text-smoke-700 hover:border-flame-500 hover:bg-flame-50 transition-all flex items-center justify-center gap-2"
      >
        <MapPin className="w-5 h-5" />
        Use My Current Location
      </button>

      {/* Check Weather Button */}
      <button
        onClick={handleCheckWeather}
        disabled={
          loading ||
          (locationType === 'zip' && !zipCode) ||
          (locationType === 'city' && (!city || !state)) ||
          (locationType === 'coords' && (!coords.lat || !coords.lon))
        }
        className="btn-secondary w-full flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-flame-600" />
            Checking Weather...
          </>
        ) : (
          <>
            <Search className="w-5 h-5" />
            Check Weather
          </>
        )}
      </button>

      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Weather Display */}
      {weather && (
        <div className="card bg-gradient-to-br from-sky-50 to-blue-50 border-2 border-sky-200">
          <h3 className="text-lg font-semibold text-smoke-900 mb-3 flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-flame-600" />
            Current Conditions
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-smoke-600 font-medium">Location</span>
              <span className="text-smoke-900 font-semibold">{weather.location.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-smoke-600 font-medium">Temperature</span>
              <span className="text-smoke-900 font-semibold">{weather.current.temperatureF.toFixed(0)}°F</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-smoke-600 font-medium">Feels Like</span>
              <span className="text-smoke-900 font-semibold">{weather.current.feelsLikeF.toFixed(0)}°F</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-smoke-600 font-medium flex items-center gap-1">
                <Wind className="w-4 h-4" />
                Wind
              </span>
              <span className="text-smoke-900 font-semibold">{weather.current.windMph.toFixed(0)} mph</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-smoke-600 font-medium flex items-center gap-1">
                <Droplets className="w-4 h-4" />
                Precipitation
              </span>
              <span className="text-smoke-900 font-semibold">{weather.current.precipitation ? 'Yes' : 'No'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="p-3 bg-smoke-100 rounded-lg text-sm text-smoke-600">
        {!weather 
          ? 'Enter your location and check weather to see current conditions. Then click Calculate below.'
          : 'Weather confirmed! Click Calculate below to see your cook plan.'
        }
      </div>
    </div>
  );
}
